import styled from "styled-components";
import png1 from "../../assets/images/upload1.png";
import png2 from "../../assets/images/upload2.png";
import { nvrTools } from "../../lib/types";

const Container = styled.div`
  display: block;
  font-weight: 300;
`;

const ItalicSpan = styled.span`
  font-style: italic;
`;

const ImageContainer = styled.div`
  width: 100%;
  text-align: center;
  margin-block: 18px 32px;
  margin-left: -40px;
`;

const StyledImage = styled.img``;

const SpacedLi = styled.li`
  margin-block: 8px;
`;

interface IProps {
  mode: nvrTools;
}

function HowToUpload({ mode }: IProps) {
  return (
    <Container>
      <p>
        To ensure compatibility with NVR Map, shapefiles need to adhere to the
        following data standards:
      </p>
      <ul>
        <SpacedLi>
          All shapefiles must use the Geocentric Datum of Australia 2020 and
          need to be in one of the following coordinate systems:
          <ul>
            <li>Geographics GDA2020 (EPSG: 7844)</li>
            <li>Vicgrid GDA2020 (EPSG:7899)</li>
            <li>MGA Zone 54 GDA2020 (EPSG: 7854)</li>
            <li>MGA Zone 55 GDA2020 (EPSG: 7855)</li>
          </ul>
        </SpacedLi>
        <SpacedLi>
          Shapefiles must be uploaded in NVR Map as .zip files. Each .zip file
          must contain the multiple files that make up one shapefile (.shp,
          .prj, .dbf, .shx).
        </SpacedLi>

        {mode === nvrTools.removal ? (
          <SpacedLi>
            When uploading Patches proposed for removal, a polygon shapefile
            must be used. Patch polygons must not overlap, self-intersect, or
            consist of multiple parts (i.e. a single feature composed of
            multiple polygons).
          </SpacedLi>
        ) : (
          <SpacedLi>
            When uploading Patches proposed for protection and/or Revegetation
            areas, a polygon shapefile must be used. Each polygon must have the
            following attributes:
            <ul>
              <li>
                A text field labelled ‘type’, specifying whether the feature is
                a ‘p’ (Patch) or ‘rv’ (Revegetation area).
              </li>
            </ul>
            <ImageContainer style={{ marginBottom: "12px" }}>
              <StyledImage
                src={png2}
                alt="Table with polygons and revegetation areas"
              />
            </ImageContainer>
            Patch/Revegetation area polygons must not overlap, self-intersect,
            or consist of multiple parts (i.e. a single feature composed of
            multiple polygons).
          </SpacedLi>
        )}

        <SpacedLi>
          When uploading Scattered Trees or Patch Trees, a point shapefile must
          be used. Each point must have the following attributes:
          <ul>
            <li>
              A numeric field labelled <ItalicSpan>‘meas’</ItalicSpan>,
              indicating the measurement of each tree point in centimetres. The
              measurement should be based on either the circumference of the
              trunk at 1.3m above ground level or the trunk diameter at breast
              height.
            </li>
            <li>
              A text field labelled <ItalicSpan>‘meas_type’</ItalicSpan>,
              specifying whether the measurement method used was{" "}
              <ItalicSpan>'cir'</ItalicSpan> (circumference) or{" "}
              <ItalicSpan>'dbh'</ItalicSpan> (diameter at breast height).
            </li>
            <ImageContainer>
              <StyledImage src={png1} alt="Table with tree measurements" />
            </ImageContainer>
          </ul>
        </SpacedLi>
        {mode === nvrTools.removal ? (
          <SpacedLi>
            Each shapefile must be packaged in a separate .zip file (i.e.
            individual .zip files for Patches and trees).
          </SpacedLi>
        ) : (
          <SpacedLi>
            Each shapefile must be packaged in a separate .zip file (i.e.
            individual .zip files for Patches/Revegetation areas and trees).
            Patches and Revegetation areas can be provided in a single shapefile
            and .zip file, with these features distinguished from each other
            using the 'type' attribute field.
          </SpacedLi>
        )}
        <SpacedLi>
          Scattered Trees and Patch Trees can be uploaded in a single
          shapefile/.zip file, as NVR Map will classify each tree point based on
          its spatial location. Tree points within Patches will be classified as
          Patch Trees, while tree points outside Patches will be classified as
          Scattered Trees.
        </SpacedLi>
      </ul>
    </Container>
  );
}

export default HowToUpload;
